import type { VRM } from '@pixiv/three-vrm'
import { useCallback, useMemo, useRef, useState } from 'react'
import type { AnimationMixer, Object3D } from 'three'

const INITIAL_SAMPLE_DELTA = 1 / 120

export type InitialMotionHandlers = {
  revealWithoutMotion: (scene?: Object3D | null) => void
  applyInitialPoseAndReveal: (vrm: VRM, mixer: AnimationMixer) => void
  isInitialMotionPrepared: () => boolean
}

type UseInitialMotionPreparationResult = {
  initialMotionPrepared: boolean
  resetInitialMotionPreparation: () => void
  onVrmLoaded: (loadedVrm: VRM) => void
  initialMotionHandlers: InitialMotionHandlers
}

/**
 * 初回ロード時だけ scene 表示を遅らせ、最初のモーション姿勢が確定してから表示する。
 */
export const useInitialMotionPreparation = (): UseInitialMotionPreparationResult => {
  const [initialMotionPrepared, setInitialMotionPrepared] = useState(false)
  const preparedRef = useRef(false)

  const revealWithoutMotion = useCallback((scene?: Object3D | null) => {
    if (scene) {
      scene.visible = true
    }

    if (preparedRef.current) return
    preparedRef.current = true
    setInitialMotionPrepared(true)
  }, [])

  const prepareSceneVisibility = useCallback((scene: Object3D) => {
    if (!preparedRef.current) {
      scene.visible = false
    }
  }, [])

  const applyInitialPoseAndReveal = useCallback(
    (vrm: VRM, mixer: AnimationMixer) => {
      if (!preparedRef.current) {
        mixer.update(INITIAL_SAMPLE_DELTA)
        vrm.update(INITIAL_SAMPLE_DELTA)
      }

      revealWithoutMotion(vrm.scene)
    },
    [revealWithoutMotion]
  )

  const resetInitialMotionPreparation = useCallback(() => {
    preparedRef.current = false
    setInitialMotionPrepared(false)
  }, [])

  const isInitialMotionPrepared = useCallback(() => preparedRef.current, [])

  const onVrmLoaded = useCallback(
    (loadedVrm: VRM) => {
      prepareSceneVisibility(loadedVrm.scene)
    },
    [prepareSceneVisibility]
  )

  const initialMotionHandlers = useMemo<InitialMotionHandlers>(
    () => ({ revealWithoutMotion, applyInitialPoseAndReveal, isInitialMotionPrepared }),
    [revealWithoutMotion, applyInitialPoseAndReveal, isInitialMotionPrepared]
  )

  return {
    initialMotionPrepared,
    resetInitialMotionPreparation,
    onVrmLoaded,
    initialMotionHandlers,
  }
}
